import { 
  Accordion,
  AccordionButton, 
  AccordionItem, 
  AccordionPanel, 
  Flex, 
  HStack, 
  Switch, 
  Text
} from "@chakra-ui/react";
import { CountdownTimer } from "components/CountdownTimer/CountdownTimer";
import IconTicket from "components/IconTicket/IconTicket";
import { 
  IconCheckWhite,
  IconChevron,
} from "icons";
import { Dispatch, SetStateAction } from "react";
import { getNextDay3PMEST } from "utils/helpers";

interface TournamentEntryAccordionProps {
  children: React.ReactNode;
  isSelected?: boolean;
  setIsSelected?: Dispatch<SetStateAction<boolean | undefined>>;
  selectedCarAvailable?: boolean;
}

export const TournamentSwitch = ({ isSelected, setIsSelected }: { isSelected?: boolean; setIsSelected?: Dispatch<SetStateAction<boolean | undefined>> }) => {
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsSelected?.(e.target.checked);
  };

  return (
    <Switch 
      variant="leaderboard" 
      size='lg'
      isChecked={isSelected}
      onChange={handleSwitchChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const TournamentEntryAccordion = ({ children, setIsSelected, isSelected, selectedCarAvailable }: TournamentEntryAccordionProps) => {
  return (
    <Flex direction="column" w="full">
      <Accordion 
        allowToggle
        variant="entryAccordion"
        w="full"
      >
        <AccordionItem 
          border="none"
          position="relative"
          background="darkVoid.100"
        >
          {({ isExpanded }) => (
            <>
              {selectedCarAvailable && (
                <Flex 
                  position="absolute"
                  right="4"
                  top={{ base: '18px', md: '22px' }}
                  zIndex={3}
                >
                  <TournamentSwitch 
                    isSelected={isSelected}
                    setIsSelected={setIsSelected}
                  />
                </Flex>
              )}

              <Flex 
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                margin="auto"
                background="linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
                width="100%"
                height="100%"
                borderRadius="4px"
                zIndex={1}
                opacity={isExpanded ? 1 : 0}
                transition="opacity 0.2s ease"
              />

              <AccordionButton
                width="full"
                justifyContent="space-between"
                alignItems={{ base: 'center', md: 'center' }}
                background="transparent"
                _hover={{
                  background: 'transparent',
                }}
                flexDirection="row"
                px={{ base: 2, md: 4 }}
                py="4"
                position="relative"
                pr={{ base: 2, md: 4 }}
              >
                <Flex 
                  position="absolute"
                  borderRadius="4px"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  margin="auto"
                  zIndex={0}
                  background="linear-gradient(90deg, #151517 40%, rgba(21, 21, 23, 0.2) 100%)"
                  width="100%"
                  height="100%"
                />

                {!isExpanded && (
                  <Flex 
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    margin="auto"
                    background="linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
                    width="100%"
                    height="100%"
                    zIndex={0}
                  />
                )}

                <HStack
                  zIndex={2}
                  gap={{ base: "2", md: "4" }}
                  justifyContent="center"
                  w="full"
                >
                  <Flex
                    background="white.10"
                    p={{ base: "2", md: "3" }}
                    borderRadius="4px"
                    transition="all 0.2s ease"
                    _hover={{
                      background: 'white.20',
                    }}
                    alignItems="center"
                    alignSelf="stretch"
                  >
                    <IconChevron 
                      boxSize={4} 
                      transform={!isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="all 0.2s ease"
                    />
                  </Flex>

                  <Flex
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                    w="full"
                    justifyContent="space-between"
                  >
                    <Flex
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={0.5}
                    >
                      <Flex
                        alignItems={{ base: 'center', md: 'center' }}
                      >
                        <Text
                          fontSize={{ base: '12px', md: '16px' }}
                          lineHeight={{ base: '14px', md: '19px' }}
                          fontWeight="600"
                          color="white.80"
                          textTransform="uppercase"
                          fontStyle="italic"
                        >
                          JOIN THE DAILY LEADERBOARD
                        </Text>
                      </Flex>
                      
                      <Flex
                        fontFamily="body"
                        textTransform="none"
                        gap="1"
                        alignItems="center"
                      >
                        <Text
                          fontSize={{ base: '12px', md: '14px' }}
                          lineHeight={{ base: '14px', md: '18px' }}
                          fontWeight="500"
                          color="treasuredTeal.100"
                          textTransform="uppercase"
                        >
                          ENTRIES CLOSE IN
                        </Text>

                        <CountdownTimer timeLeft={getNextDay3PMEST()} />
                      </Flex>
                    </Flex>

                    <Flex
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={2}
                    >
                      {selectedCarAvailable ? (
                        <Flex
                          flexDir="column"
                          alignItems="flex-center"
                          mr={{ base: 16, md: 16 }}
                        >
                          <Flex
                            alignItems="center"
                          >
                            <Text
                              fontSize={{ base: '24px', md: '28px' }}
                              lineHeight={{ base: '24px', md: '28px' }}
                              fontWeight="600"
                              color="white"
                            >
                              1
                            </Text> 
                            <IconTicket
                              width={{ base: '1.25rem', md: '1.5rem' }}
                              height={{ base: '1.25rem', md: '1.5rem' }}
                            />
                          </Flex>
                          <Text
                            fontSize="8px"
                            lineHeight="8px"
                            fontWeight="600"
                            color="cerebralGrey.100"
                          >
                            ENTRY
                          </Text>
                        </Flex> 
                      ) : (
                        <Flex
                          background="linear-gradient(90.65deg, rgba(0, 255, 232, 0.4) 0.56%, rgba(0, 255, 232, 0) 99.44%)"
                          borderRadius="4px"
                          position="relative"
                          _before={{
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            borderRadius: "4px",
                            borderRightRadius: "0px",
                            padding: "2px",
                            paddingRight: "0px",
                            background: "linear-gradient(90.65deg, rgba(0, 255, 232, 0.4) 0.56%, rgba(0, 255, 232, 0) 99.44%)",
                            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                            maskComposite: "exclude",
                          }}
                          borderRight="none"
                          gap="1"
                          shadow="0px 2px 6px 2px rgba(0, 0, 0, 0.15)"
                          alignItems="center"
                          px="8px"
                          pr="12px"
                          py="8px"
                        >
                          <IconCheckWhite 
                            __css={{
                              path: {
                                fill: "rgba(0, 255, 232, 1)"
                              }
                            }}
                            boxSize={4}
                          />

                          <Text
                            fontSize="14px"
                            lineHeight="18px"
                            fontWeight="600"
                            color="rgba(0, 255, 232, 1)"
                          >
                            JOINED
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                </HStack>
              </AccordionButton>
              <AccordionPanel 
                overflow="auto"
                pt="4"
                px={{ base: 2, md: 4 }}
                background="transparent"
              >
                {children}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Flex>
  );
};

export default TournamentEntryAccordion;